// Login Modal Component with social authentication
import React, { useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { resendConfirmation } from '../../auth/authService';
import { debugLog } from '../../utils/debug';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
}

type AuthMode = 'signin' | 'signup';

export const LoginModal: React.FC<LoginModalProps> = ({ 
  isOpen, 
  onClose, 
  title = "fr3t dot net",
  subtitle = "Sign in to save your progressions"
}) => {
  const { signInWithEmail, signUpWithEmail } = useAuth();
  // TODO: Re-enable OAuth when providers are configured
  // const { signInWithGoogle, signInWithApple, signInWithEmail, signUpWithEmail } = useAuth();
  
  const [authMode, setAuthMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResendButton, setShowResendButton] = useState(false);

  const handleClose = () => {
    setError(null);
    setEmail('');
    setPassword('');
    setAuthMode('signin');
    setShowResendButton(false);
    onClose();
  };

  const handleResendConfirmation = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      debugLog.info('Attempting to resend confirmation email for:', email);
      await resendConfirmation(email);
      debugLog.success('Resend confirmation request completed');
      setError('Confirmation email sent! Please check your inbox.');
      setShowResendButton(false);
    } catch (error) {
      debugLog.error('Resend confirmation failed:', error);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Failed to resend confirmation email. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // OAuth handlers - temporarily disabled
  // TODO: Re-enable when OAuth providers are properly configured
  /*
  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await signInWithGoogle();
      // OAuth will redirect
    } catch {
      setError('Failed to sign in with Google. Please try again.');
      setIsLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await signInWithApple();
      // OAuth will redirect
    } catch {
      setError('Failed to sign in with Apple. Please try again.');
      setIsLoading(false);
    }
  };
  */

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      if (authMode === 'signin') {
        debugLog.info('Attempting email sign in for:', email);
        await signInWithEmail(email, password);
        debugLog.success('Email sign in successful');
        handleClose();
      } else {
        debugLog.info('Attempting email signup for:', email);
        await signUpWithEmail(email, password);
        debugLog.success('Email signup completed');
        // Show confirmation message for signup
        setError('Please check your email and click the confirmation link to complete your signup.');
        setShowResendButton(true);
        setIsLoading(false);
        // Don't close modal - let user see the message
      }
      
    } catch (error) {
      debugLog.error('Email auth failed:', {
        mode: authMode,
        email: email,
        error: error
      });
      
      if (error instanceof Error) {
        // Handle specific email confirmation errors
        if (error.message.includes('Email not confirmed')) {
          setError('Please check your email and click the confirmation link before signing in.');
          setShowResendButton(true);
        } else if (error.message.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please try again.');
          setShowResendButton(false);
        } else {
          setError(error.message);
          setShowResendButton(false);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
        setShowResendButton(false);
      }
      setIsLoading(false);
    }
  };

  const toggleAuthMode = () => {
    setAuthMode(authMode === 'signin' ? 'signup' : 'signin');
    setError(null);
    setShowResendButton(false);
  };

  if (!isOpen) return null;

  return (
    <div className="account-modal-overlay" onClick={handleClose}>
      <div className="account-modal login-modal" onClick={(e) => e.stopPropagation()}>
        <div className="account-modal-header">
          <h2 className="account-modal-title">{title}</h2>
        </div>
        
        <div className="account-modal-content">
          <p className="login-modal-subtitle">{subtitle}</p>

          {error && (
            <div className="error-message">
              {error}
              {showResendButton && (
                <button 
                  type="button" 
                  onClick={handleResendConfirmation}
                  disabled={isLoading}
                  className="resend-confirmation-btn"
                  style={{ 
                    marginTop: '10px', 
                    padding: '8px 16px', 
                    fontSize: '14px',
                    backgroundColor: '#4f46e5',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {isLoading ? 'Sending...' : 'Resend confirmation email'}
                </button>
              )}
            </div>
          )}

        {/* Social login buttons - temporarily disabled */}
        {/* TODO: Re-enable when OAuth providers are properly configured
        <div className="social-login-buttons">
          <button 
            className="social-login-btn google"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="loading-spinner" />
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </>
            )}
          </button>

          <button 
            className="social-login-btn apple"
            onClick={handleAppleSignIn}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="loading-spinner" />
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 21.99 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 21.99C7.79 22.03 6.8 20.68 5.96 19.47C4.25 17 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
                </svg>
                Continue with Apple
              </>
            )}
          </button>
        </div>
        */}

        {/* Login divider - hidden since social logins are disabled */}
        {/* 
        <div className="login-divider">
          <span>or</span>
        </div>
        */}

        <form className="email-form" onSubmit={handleEmailAuth}>
          <div className="form-group">
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              disabled={isLoading}
            />
          </div>

          <button type="submit" className="email-submit-btn" disabled={isLoading}>
            {isLoading ? (
              <div className="loading-spinner" />
            ) : (
              authMode === 'signin' ? 'Sign In' : 'Create Account'
            )}
          </button>
        </form>

        <div className="auth-toggle">
          {authMode === 'signin' ? (
            <p>
              Don't have an account?{' '}
              <button type="button" onClick={toggleAuthMode}>
                Sign up
              </button>
            </p>
          ) : (
            <p>
              Already have an account?{' '}
              <button type="button" onClick={toggleAuthMode}>
                Sign in
              </button>
            </p>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};
